import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toastService } from '../../services/toast';

const GatewayPage = ({ onSuccess }) => {
  const [code, setCode] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();
  
  // الكود السري للدخول - في تطبيق حقيقي نضعه في البيئة أو قاعدة البيانات
  const SECRET_CODE = '1379';
  
  // إضافة استماع للكيبورد لإدخال الأرقام
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // لو ضغط على رقم
      if (e.key >= '0' && e.key <= '9' && code.length < 4) {
        setCode(prevCode => prevCode + e.key);
      } 
      // لو ضغط على Backspace
      else if (e.key === 'Backspace' || e.key === 'Delete') {
        handleDelete();
      } 
      // لو ضغط على مسح الكل
      else if (e.key === 'Escape') {
        handleClear();
      } 
      // لو ضغط على Enter للتأكيد
      else if (e.key === 'Enter') {
        handleSubmit();
      }
    };
    
    // إضافة المستمع لأحداث ضغط المفاتيح
    window.addEventListener('keydown', handleKeyDown);
    
    // إزالة المستمع عند تفكيك المكون
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [code]); // الاعتماد على كود للتحديث عند تغييره
  
  const handleButtonClick = (value) => {
    if (code.length < 4) {
      setCode(prevCode => prevCode + value);
    }
  };
  
  const handleClear = () => {
    setCode('');
  };
  
  const handleDelete = () => {
    setCode(prevCode => prevCode.slice(0, -1));
  };
  
  const handleSubmit = () => {
    if (code === SECRET_CODE) {
      // تسجيل أن المستخدم أدخل الكود الصحيح في localStorage
      localStorage.setItem('gateway_access', JSON.stringify({
        granted: true,
        timestamp: new Date().getTime()
      }));
      
      // الانتظار لانتهاء الأنيميشن قبل الانتقال
      setIsAnimating(true);
      
      // إذا كانت onSuccess متوفرة (في حالة الاستخدام مع SecureAdminLogin)
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } else {
      toastService.error('الكود غير صحيح');
      handleClear();
    }
  };
  
  // تخطيط الأزرار بشكل مشابه للآلة الحاسبة
  const buttons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['C', '0', '⌫']
  ];
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none section-bg">
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/10 dark:bg-blue-500/20 rounded-full blur-3xl"
        />
      </div>

      <motion.div 
        className="max-w-md w-full space-y-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-8 rounded-2xl shadow-2xl border border-gray-300 dark:border-gray-700"
        animate={isAnimating ? { opacity: 0 } : { opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            نظام الحماية المتقدم
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            أدخل رمز الدخول للوصول إلى لوحة التحكم
          </p>
        </div>
        
        {/* Display */}
        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-xl mb-6 text-center">
          <div className="text-2xl font-mono tracking-widest h-8">
            {code ? '*'.repeat(code.length) : ''}
          </div>
        </div>
        
        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3">
          {buttons.map((row, rowIndex) => (
            <React.Fragment key={`row-${rowIndex}`}>
              {row.map((btn) => (
                <motion.button
                  key={btn}
                  whileTap={{ scale: 0.95 }}
                  className={`p-4 rounded-xl text-xl font-semibold transition-colors
                    ${btn === 'C' ? 'bg-red-500 hover:bg-red-600 text-white' : 
                      btn === '⌫' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 
                      'bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white'
                    }`}
                  onClick={() => {
                    if (btn === 'C') handleClear();
                    else if (btn === '⌫') handleDelete();
                    else handleButtonClick(btn);
                  }}
                >
                  {btn}
                </motion.button>
              ))}
            </React.Fragment>
          ))}
        </div>
        
        {/* Submit Button */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          className="mt-6 w-full bg-blue-500 text-white p-4 rounded-xl text-xl font-semibold hover:bg-blue-600 transition-colors"
          onClick={handleSubmit}
        >
          دخول
        </motion.button>
      </motion.div>
    </div>
  );
};

export default GatewayPage;
